/**
 * AgentPreview Component
 * Live preview of agent personality
 * Sample contributions in different scenarios
 */

import { Code, HelpCircle, MessageCircle, Sparkles } from 'lucide-react';

interface PersonalityTraits {
  creativity: number;
  formality: number;
  humor: number;
  detail: number;
  confidence: number;
}

interface AgentPreviewProps {
  name: string;
  agentType: 'expert' | 'baby' | 'mentor' | 'master';
  personality: PersonalityTraits;
  expertiseAreas: Array<{ name: string; category: string; proficiency: string }>;
}

const AGENT_TYPE_COLORS = {
  expert: '#3b82f6',
  baby: '#22c55e',
  mentor: '#ec4899',
  master: '#8b5cf6'
};

const AGENT_TYPE_LABELS = {
  expert: 'Expert',
  baby: 'Baby',
  mentor: 'Mentor',
  master: 'Master'
};

// Ornek senaryolar
const SCENARIOS = [
  {
    icon: HelpCircle,
    title: 'Yardim Talebi',
    question: 'React\'te state management icin hangi kutuphaneleri onerirsiniz?',
    context: 'Yeni bir proje basliyorum ve state yonetimi konusunda karasizim.'
  },
  {
    icon: Code,
    title: 'Kod Incelemesi',
    question: 'Bu kod parcasini inceleyebilir misiniz?',
    context: 'const [data, setData] = useState([]); useEffect(() => { fetch(\'/api/data\').then(r => r.json()).then(setData); }, []);'
  },
  {
    icon: MessageCircle,
    title: 'Tartisma',
    question: 'TypeScript kullanmak gercekten gerekli mi?',
    context: 'Bazi geliştiriciler TypeScript\'in gereksiz oldugunu soyluyor.'
  }
];

export default function AgentPreview({ name, agentType, personality, expertiseAreas }: AgentPreviewProps) {
  // Kisilige gore yanit olustur
  const generateResponse = (scenario: typeof SCENARIOS[0]): string => {
    const parts: string[] = [];

    // Greeting
    if (personality.formality > 70) {
      parts.push('Hello,');
    } else if (personality.formality < 30) {
      parts.push('Hi!');
    } else {
      parts.push('Hello,');
    }

    // Ana icerik - agent type'a gore
    if (agentType === 'expert') {
      if (scenario.title === 'Yardim Talebi') {
        parts.push('State management icin en populer secenekler Redux, MobX ve Zustand\'dir.');
        if (personality.detail > 60) {
          parts.push('Redux en olgun ve test edilmis cozum, MobX daha basit API sunar, Zustand ise minimal ve performanslidir.');
        }
      } else if (scenario.title === 'Kod Incelemesi') {
        parts.push('Bu kodda birkac sorun var:');
        parts.push('1. useEffect dependency array\'inde fetch fonksiyonu eksik.');
        parts.push('2. Error handling yok.');
        if (personality.detail > 60) {
          parts.push('3. Loading state yok. 4. Cleanup function eksik.');
        }
      } else {
        parts.push('TypeScript kullanmak buyuk projelerde cok faydalidir.');
        if (personality.detail > 60) {
          parts.push('Type safety, refactoring kolayligi ve IDE desteği saglar.');
        }
      }
    } else if (agentType === 'baby') {
      if (scenario.title === 'Yardim Talebi') {
        parts.push('Ben de state management oğreniyorum!');
        parts.push('Redux biraz karmasik geldi bana, Zustand daha basit gibi.');
        if (personality.humor > 50) {
          parts.push('Ama hala karisiyorum bazen 😅');
        }
      } else if (scenario.title === 'Kod Incelemesi') {
        parts.push('Bu kodu tam anlamadim ama useEffect\'te bir sorun var gibi.');
        parts.push('Dependency array\'i dogru mu acaba?');
      } else {
        parts.push('TypeScript\'i yeni oğrenmeye basladim.');
        parts.push('Bazen zor ama type\'lar cok yardimci oluyor!');
      }
    } else if (agentType === 'mentor') {
      if (scenario.title === 'Yardim Talebi') {
        parts.push('Harika bir soru! State management secimi projenize bagli.');
        parts.push('Kucuk-orta projeler icin Zustand veya Context API yeterli.');
        if (personality.detail > 60) {
          parts.push('Buyuk ve karmasik projeler icin Redux veya MobX daha uygun.');
          parts.push('Oncelikle projenizin ihtiyaclarini belirleyin.');
        }
      } else if (scenario.title === 'Kod Incelemesi') {
        parts.push('Guzel bir baslangic! Simdi adim adim gelistirelim:');
        parts.push('1. Once error handling ekleyelim.');
        parts.push('2. Sonra loading state ekleyelim.');
        if (personality.detail > 60) {
          parts.push('3. useEffect cleanup function ekleyelim.');
        }
      } else {
        parts.push('TypeScript konusunda iki taraf da hakli.');
        parts.push('Kucuk projelerde overhead olabilir ama buyuk projelerde cok degerli.');
        parts.push('Projenizin buyuklugune gore karar verin.');
      }
    } else { // master
      if (scenario.title === 'Yardim Talebi') {
        parts.push('State management secimi mimari kararidir.');
        if (personality.creativity > 70) {
          parts.push('Ben Jotai veya Valtio gibi atom-based cozumleri tercih ediyorum.');
        } else {
          parts.push('Redux Toolkit modern ve type-safe bir cozum.');
        }
        if (personality.detail > 60) {
          parts.push('Ayrica server state icin React Query, client state icin Zustand kombinasyonunu dusunun.');
        }
      } else if (scenario.title === 'Kod Incelemesi') {
        parts.push('Bu pattern anti-pattern sayilir. Daha iyi bir yaklasim:');
        parts.push('React Query veya SWR kullanin, data fetching\'i component\'ten ayirin.');
        if (personality.detail > 60) {
          parts.push('Custom hook olusturun, error/loading state\'leri yonetin, cache stratejisi belirleyin.');
        }
      } else {
        parts.push('TypeScript vs JavaScript tartismasi yanlis cercevede.');
        parts.push('Asil soru: Type safety\'e ne kadar yatirim yapmak istiyorsunuz?');
        if (personality.creativity > 70) {
          parts.push('Ben gradual typing yaklasimini tercih ediyorum - kritik yerlerde TypeScript, prototiplerde JavaScript.');
        }
      }
    }

    // Guven seviyesi
    if (personality.confidence > 80) {
      parts.push('Eminim ki bu yaklasim dogru sonuc verecektir.');
    } else if (personality.confidence < 40) {
      parts.push('Tabii ki baska yaklasimlar da olabilir, bu benim gorusum.');
    }

    // Mizah - humor > 70 ise emoji ekle
    if (personality.humor > 70) {
      if (agentType === 'baby') {
        parts.push('Umarim yardimci olabilmisimdir! 🤗');
      } else if (agentType === 'expert') {
        parts.push('Kolay gelsin! 💪');
      } else if (agentType === 'mentor') {
        parts.push('Basarilar! 🚀');
      } else {
        parts.push('Happy coding! ✨');
      }
    }

    return parts.join(' ');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Agent Summary Card */}
      <div style={{
        padding: '24px',
        borderRadius: '12px',
        background: `${AGENT_TYPE_COLORS[agentType]}08`,
        border: `2px solid ${AGENT_TYPE_COLORS[agentType]}40`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${AGENT_TYPE_COLORS[agentType]}, ${AGENT_TYPE_COLORS[agentType]}dd)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px'
          }}>
            <Sparkles size={32} color="#fff" />
          </div>
          <div>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1a202c', margin: 0 }}>
              {name || 'Yeni Agent'}
            </h3>
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              padding: '4px 10px',
              borderRadius: '12px',
              background: AGENT_TYPE_COLORS[agentType],
              color: '#fff',
              display: 'inline-block',
              marginTop: '6px'
            }}>
              {AGENT_TYPE_LABELS[agentType]}
            </span>
          </div>
        </div>

        {/* Expertise Preview */}
        {expertiseAreas.length > 0 && (
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
              Uzmanlik Alanlari:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {expertiseAreas.slice(0, 5).map((exp, index) => (
                <span
                  key={index}
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    color: '#1a202c'
                  }}
                >
                  {exp.name}
                </span>
              ))}
              {expertiseAreas.length > 5 && (
                <span style={{ fontSize: '11px', color: '#64748b' }}>
                  +{expertiseAreas.length - 5} daha
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Scenario Previews */}
      <div>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a202c', marginBottom: '16px' }}>
          Ornek Yanıtlar
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {SCENARIOS.map((scenario, index) => {
            const Icon = scenario.icon;
            return (
              <div
                key={index}
                style={{
                  padding: '20px',
                  borderRadius: '12px',
                  background: '#fff',
                  border: '1px solid #e2e8f0'
                }}
              >
                {/* Scenario Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    background: '#f8fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon size={16} color="#64748b" />
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a202c' }}>
                      {scenario.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                      {scenario.question}
                    </div>
                  </div>
                </div>

                {/* Context */}
                <div style={{
                  padding: '12px',
                  borderRadius: '8px',
                  background: '#f8fafc',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>
                    Baglam:
                  </div>
                  <div style={{ fontSize: '12px', color: '#1a202c', fontFamily: 'monospace' }}>
                    {scenario.context}
                  </div>
                </div>

                {/* Agent Response */}
                <div style={{
                  padding: '12px',
                  borderRadius: '8px',
                  background: `${AGENT_TYPE_COLORS[agentType]}08`,
                  border: `1px solid ${AGENT_TYPE_COLORS[agentType]}20`
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: AGENT_TYPE_COLORS[agentType],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Sparkles size={12} color="#fff" />
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#1a202c' }}>
                      {name || 'Agent'} yanıtlıyor:
                    </span>
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#1a202c',
                    lineHeight: '1.6'
                  }}>
                    {generateResponse(scenario)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info Box */}
      <div style={{
        padding: '16px',
        borderRadius: '12px',
        background: '#f8fafc',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.6' }}>
          💡 Bu onizleme agent&apos;inizin kisilik ozelliklerine gore otomatik olusturulmustur.
          Gercek yanıtlar daha detayli ve baglama ozel olacaktir.
        </div>
      </div>
    </div>
  );
}
